import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Globe, ShieldCheck, Lock, Inbox, BarChart3, Database, ExternalLink, Activity, CheckCircle2, Clock, AlertTriangle, MessageCircle, FileText, Boxes, ChevronRight, TrendingUp, LifeBuoy, Phone, Mail, Loader2, Info } from "lucide-react";
import api from "@/lib/api";
import { useCockpit } from "./CockpitLayout";
import { Card, Sparkline, BtnPrimary, fmt, SectionLabel, Badge, WebsitePreview, HealthRing, healthScore, healthColor, healthBand, AnalyticsOffline } from "./ui";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { chf } from "@/components/common";
import { PaketCard, MonatsreportCard } from "./Overview";
import { EditPencil, EditDialog, toDateInput } from "./CockpitEdit";
import { useI18n } from "@/context/I18nContext";
import { TrackingConnect } from "./TrackingConnect";
import { websiteReachabilityVerified } from "../../lib/environmentEvidence";

const WEBSITE_STATUS_OPTS = [
  { value: "online", label: "Online" },
  { value: "in_pruefung", label: "In Vorbereitung" },
  { value: "offline", label: "Offline" },
];

const SUPPORT_PHONE = "+41 31 911 10 85";
const SUPPORT_EMAIL = "support@kewagpage.ch";

const TONE = (accent) => ({
  green: { tone: "green", hex: accent, word: "Aktiv" }, live: { tone: "green", hex: accent, word: "Aktiv" },
  amber: { tone: "amber", hex: "#fbbf24", word: "In Prüfung" }, check: { tone: "amber", hex: "#fbbf24", word: "In Prüfung" },
  red: { tone: "red", hex: "#f87171", word: "Fehler" }, error: { tone: "red", hex: "#f87171", word: "Fehler" },
  gray: { tone: "slate", hex: "#9ca3af", word: "Inaktiv" }, none: { tone: "slate", hex: "#9ca3af", word: "Inaktiv" },
});

const fmtDateTime = (s) => { try { return s ? new Date(s).toLocaleString("de-CH", { dateStyle: "short", timeStyle: "short" }) : ""; } catch { return ""; } };
const fmtDate = (s) => { try { return s ? new Date(s).toLocaleDateString("de-CH") : ""; } catch { return ""; } };

export default function CWebsite() {
  const { overview, accent, base, clientId, canEdit, refresh } = useCockpit();
  const { t } = useI18n();
  const nav = useNavigate();
  const [heroEdit, setHeroEdit] = useState(false);
  const [liveSite, setLiveSite] = useState(null);
  const [checkResult, setCheckResult] = useState(null);
  const [checkAttempt, setCheckAttempt] = useState(0);
  const scopeKey = clientId || overview.client?.id || "self";
  const checking = checkResult?.scopeKey !== scopeKey || checkResult?.status === "loading";
  const checkFailed = checkResult?.scopeKey === scopeKey && checkResult.status === "failed";
  const w = (liveSite?.scopeKey === scopeKey ? liveSite.site : null) || overview.website || {};

  // Echte Live-Prüfung beim Laden (Sicherheits- & Verbindungs-Checks immer live).
  // Server-seitige 90s-Frische schützt die Zielseite vor Hammering.
  useEffect(() => {
    let active = true;
    setCheckResult({ scopeKey, status: "loading" });
    api.post(`/cockpit/website/check?auto=true${clientId ? `&client_id=${clientId}` : ""}`)
      .then(({ data }) => {
        if (!data || typeof data !== "object" || Array.isArray(data) || !Number.isFinite(Date.parse(data.last_checked || ""))) {
          throw new Error("Missing website check evidence");
        }
        if (active) { setLiveSite({ scopeKey, site: data }); setCheckResult({ scopeKey, status: "checked" }); }
      })
      .catch(() => { if (active) setCheckResult({ scopeKey, status: "failed" }); });
    return () => { active = false; };
  }, [clientId, scopeKey, checkAttempt]);

  const reloadAnalytics = () => {
    setCheckAttempt(n => n + 1);
    if (refresh) refresh();
  };
  const st = w.states || {};
  const health = healthScore(st);
  const map = TONE(accent);
  const tn = (s) => map[s?.color] || map[s?.state] || map.gray;
  const hasUrl = !!w.public_url;
  const online = websiteReachabilityVerified(w);
  const analyticsOn = st.analytics?.color === "green" || !!w.analytics_connected;
  const initial = (w.domain || "K").replace(/^www\./, "").charAt(0).toUpperCase();
  const visits = overview.visits;
  const series = overview.visits_series;
  const delta = overview.delta_pct;
  const headTone = online ? map.green : (st.reachability?.color === "red" ? map.red : map.amber);
  const liveState = (w.live_status === "offline" || st.reachability?.color === "red") ? "offline" : (online ? "online" : "warning");
  const visit = () => hasUrl && window.open(w.public_url, "_blank", "noopener,noreferrer");

  const saveHero = async (form) => {
    await Promise.all([
      api.put(`/clients/${clientId}`, { domain: form.domain || "" }),
      api.put(`/websites/${clientId}`, {
        public_url: form.public_url || "",
        status: form.status || "in_pruefung",
        last_content_update: form.last_content_update || "",
        last_backup: form.last_backup || "",
      }),
    ]);
    setLiveSite(null);
    await refresh();
  };

  const checks = [
    { key: "reachability", icon: Globe, label: t("Erreichbarkeit"), s: st.reachability },
    { key: "ssl", icon: Lock, label: t("Sicherheit (SSL)"), s: st.ssl },
    { key: "wordpress", icon: Boxes, label: t("WordPress"), s: st.wordpress },
    { key: "forms", icon: Inbox, label: t("Formulare"), s: st.forms },
    { key: "analytics", icon: BarChart3, label: t("Besuchertracking"), s: st.analytics },
    { key: "backup", icon: Database, label: t("Backup"), s: st.backup },
  ];

  return (
    <div className="fade-in space-y-5">
      {/* ===== Header (Titel + Untertitel, exakt wie Master) ===== */}
      <div data-testid="cw-header">
        <p className="text-sm font-semibold flex items-center gap-1.5" style={{ color: accent }}><Globe className="w-4 h-4" /> {t("Website")}</p>
        <h1 className="text-[26px] lg:text-[34px] leading-tight font-black tracking-tight mt-1">{t("Website-Status")}</h1>
        <p className="text-sm text-gray-400 mt-1.5 max-w-2xl">{t("Ihre Website im Überblick — Status, Sicherheit, Backups und Performance, live durch KEWAG betreut.")}</p>
      </div>

      {/* ===== Hauptraster: links Inhalt (8) · rechts Paket/Monatsreport/Support (4) ===== */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-5 items-start">
        <div className="xl:col-span-8 space-y-5">
          {/* Hero: Status links · echte Live-Vorschau rechts */}
          <Card className="p-5 lg:p-7 relative overflow-hidden" data-testid="cw-hero">
            <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full blur-3xl opacity-10 pointer-events-none" style={{ background: headTone.hex }} />
            {canEdit && <EditPencil onClick={() => setHeroEdit(true)} testid="edit-website-hero" accent={accent} />}
            <div className="relative grid grid-cols-1 lg:grid-cols-[minmax(0,0.66fr)_minmax(0,1fr)] gap-6 lg:gap-8 items-center">
              <div className="flex flex-col gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: `${headTone.hex}1F`, color: headTone.hex }}><Globe className="w-6 h-6" /></div>
                  <div className="min-w-0">
                    <div className="text-lg lg:text-xl font-black leading-tight">{online ? t("Website erreichbar") : liveState === "offline" ? t("Website nicht erreichbar") : hasUrl ? t("Aktueller Status offen") : t("Keine Website hinterlegt")}</div>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      <span className="text-xs text-gray-400 flex items-center gap-1.5"><Lock className="w-3 h-3" style={{ color: accent }} /> {hasUrl ? w.domain : t("Bitte WordPress-Website hinterlegen.")}</span>
                      {online && <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full" style={{ background: `${accent}1A`, color: accent }} data-testid="cw-live-badge"><span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: accent }} /> {t("Live & erreichbar")}</span>}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-5">
                  <div className="w-full sm:flex-1 space-y-2.5 order-2 sm:order-1">
                    <Fact label={t("Antwortzeit")} value={w.response_ms != null ? `${w.response_ms} ms` : "—"} icon={Activity} accent={accent} />
                    <Fact label={t("Zuletzt geprüft")} value={fmtDateTime(w.last_checked) || "—"} icon={Clock} accent={accent} />
                    <Fact label={t("Zuletzt aktualisiert")} value={fmtDate(w.last_content_update) || "—"} icon={FileText} accent={accent} />
                    <Fact label={t("Letztes Backup")} value={fmtDate(w.last_backup) || t("Nicht geprüft")} icon={Database} accent={accent} />
                  </div>
                  <div className="shrink-0 flex flex-col items-center gap-2 order-1 sm:order-2" data-testid="cw-health">
                    <HealthRing score={health} size={200} stroke={12} color={healthColor(health)} valueColor="#ffffff" labelColor={healthColor(health)} label={healthBand(health, t)} />
                    <span className="text-[10px] uppercase tracking-wider text-gray-500 flex items-center gap-1">{t("Website-Gesundheit")}<HealthInfo accent={accent} t={t} /></span>
                  </div>
                </div>

                <div className="rounded-xl border p-3 flex items-center gap-2.5" style={{ borderColor: `${accent}26`, background: `${accent}0D` }} data-testid="cw-reassure">
                  <ShieldCheck className="w-4 h-4 shrink-0" style={{ color: accent }} />
                  <span className="text-xs text-gray-300">{t("KEWAG betreut Ihre Website")}</span>
                </div>
              </div>

              <div className="flex items-center justify-center w-full">
                <WebsitePreview url={hasUrl ? w.public_url : null} domain={hasUrl ? w.domain : null} initial={initial} accent={accent} t={t} live={online} maxWidthClass="max-w-none" />
              </div>
            </div>

            {Array.isArray(w.warnings) && w.warnings.length > 0 && (
              <div className="relative mt-5 flex items-start gap-2 rounded-xl border border-amber-400/30 bg-amber-400/10 p-3 text-xs text-amber-200" data-testid="cw-warnings">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <div><div className="font-semibold mb-0.5">{t("Offene Punkte")}</div><ul className="list-disc list-inside space-y-0.5">{w.warnings.map((x, i) => <li key={i}>{x}</li>)}</ul></div>
              </div>
            )}

            <div className="relative mt-5 flex items-center gap-3 flex-wrap">
              <BtnPrimary accent={accent} className={`flex-1 min-w-[180px] ${!hasUrl ? "opacity-50 cursor-not-allowed" : ""}`} data-testid="cw-visit" disabled={!hasUrl} onClick={visit}><ExternalLink className="w-4 h-4" /> {t("Website besuchen")}</BtnPrimary>
              <button onClick={() => nav(`${base}/performance`)} data-testid="cw-stats" className="px-5 py-3 rounded-xl text-sm font-semibold bg-white/5 border border-white/10 text-gray-200 hover:bg-white/10 transition-colors flex items-center gap-2"><BarChart3 className="w-4 h-4" /> {t("Statistik & Besuche")}</button>
              <button onClick={() => nav(`${base}/support`)} data-testid="cw-report-change" className="px-5 py-3 rounded-xl text-sm font-semibold bg-white/5 border border-white/10 text-gray-200 hover:bg-white/10 transition-colors flex items-center gap-2"><MessageCircle className="w-4 h-4" /> {t("Änderung melden")}</button>
            </div>

            <EditDialog open={heroEdit} onClose={() => setHeroEdit(false)} title="Website bearbeiten" accent={accent} testid="edit-website-dialog"
              onSave={saveHero}
              initial={{ domain: w.domain || "", public_url: w.public_url || "", status: w.live_status || "in_pruefung", last_content_update: toDateInput(w.last_content_update), last_backup: toDateInput(w.last_backup) }}
              fields={[
                { key: "domain", label: "Domain", type: "text", placeholder: "firma.ch" },
                { key: "public_url", label: "Website-URL", type: "url", placeholder: "https://firma.ch" },
                { key: "status", label: "Status", type: "select", options: WEBSITE_STATUS_OPTS },
                { key: "last_content_update", label: "Inhalt aktualisiert am", type: "date" },
                { key: "last_backup", label: "Letztes Backup am", type: "date" },
              ]} />
          </Card>

          {/* Sicherheits- & Verbindungs-Checks (6er-Raster) — immer live geprüft */}
          <div>
            <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
              <SectionLabel>{t("Sicherheits- & Verbindungs-Checks")}</SectionLabel>
              <span className="text-[11px] flex items-center gap-1.5 mb-3" data-testid="cw-live-indicator">
                {checking ? (
                  <span className="flex items-center gap-1.5 text-gray-400"><Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: accent }} /> {t("Live wird geprüft …")}</span>
                ) : checkFailed ? (
                  <span className="text-amber-300 flex flex-wrap items-center gap-2" role="status">{t("Aktuelle Prüfung fehlgeschlagen. Letzter bekannter Stand wird angezeigt.")}
                    <button type="button" className="underline py-2" data-testid="cw-check-retry" onClick={() => setCheckAttempt(n => n + 1)}>{t("Erneut versuchen")}</button>
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 text-gray-400">{t("Zuletzt geprüft")} · {fmtDateTime(w.last_checked) || "—"}</span>
                )}
              </span>
            </div>
            <div className="rounded-xl border border-white/5 bg-white/[0.02] px-3.5 py-2.5 mb-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-gray-400" data-testid="cw-legend">
              <span className="text-gray-200 font-semibold">{t("Echte Prüfung der Website.")}</span>
              <LegendItem color={accent} label={t("Grün = live geprüft")} />
              <LegendItem color="#fbbf24" label={t("Gelb = manuell/Prüfung")} />
              <LegendItem color="#f87171" label={t("Rot = Fehler")} />
              <LegendItem color="#9ca3af" label={t("Grau = nicht verbunden")} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cw-checks">
              {checks.map((c) => {
                const ct = tn(c.s);
                return (
                  <Card key={c.key} className="p-5 flex flex-col" data-testid={`cw-check-${c.key}`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${ct.hex}1F`, color: ct.hex }}><c.icon className="w-5 h-5" /></span>
                      <Badge tone={ct.tone} accent={accent}>{t(ct.word)}</Badge>
                    </div>
                    <div className="font-bold text-[15px]">{c.label}</div>
                    <div className="text-sm text-gray-300 mt-0.5">{t(c.s?.label || "Nicht verbunden")}</div>
                    {c.s?.detail && <div className="text-[11px] text-gray-500 mt-0.5 truncate">{c.s.detail}</div>}
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Besuche (30 Tage) */}
          <Card className="p-5 flex flex-col" data-testid="cw-performance">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2"><TrendingUp className="w-5 h-5" style={{ color: accent }} /><span className="font-bold">{t("Besuche (30 Tage)")}</span></div>
              <button onClick={() => nav(`${base}/performance`)} className="text-xs font-semibold flex items-center gap-1" style={{ color: accent }} data-testid="cw-perf-detail">{t("Detaillierte Statistik")} <ChevronRight className="w-3.5 h-3.5" /></button>
            </div>
            {analyticsOn ? (
              <>
                <div className="flex items-end gap-4 mb-2">
                  <div>
                    <div className="text-3xl font-black tracking-tight">{fmt(visits)}</div>
                    <div className="text-xs text-gray-500">{t("Besuche gesamt")}</div>
                  </div>
                  {delta != null && <span className="text-sm font-bold text-green-400 mb-1">+{delta}% <span className="text-[11px] text-gray-500 font-normal">{t("vs. Vormonat")}</span></span>}
                </div>
                <div className="flex-1 min-h-[140px]"><Sparkline data={series} color={accent} height={160} /></div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center min-h-[160px]"><AnalyticsOffline t={t} minH={160} testid="cw-perf-offline" action={<TrackingConnect clientId={clientId} accent={accent} onConnected={reloadAnalytics} />} /></div>
            )}
          </Card>
        </div>

        {/* Rechte Spalte: Ihr Paket · Monatsreport · Brauchen Sie Unterstützung? */}
        <div className="xl:col-span-4 space-y-5">
          <PaketCard pkg={overview.package} subscription={overview.subscription} billing={overview.billing} included_features={overview.included_features} active_modules={overview.active_modules} accent={accent} t={t} chf={chf} onManage={() => nav(`${base}/invoices`)} />
          <MonatsreportCard report={overview.latest_report} visits={visits} delta={delta} series={series} leads={overview.open_leads} connected={analyticsOn} accent={accent} t={t} onView={() => nav(`${base}/reports`)} />
          <SupportCallout accent={accent} t={t} onMessage={() => nav(`${base}/support`)} />
        </div>
      </div>

      {/* ===== Letzte Änderungen (volle Breite) ===== */}
      <Card className="p-5" data-testid="cw-activity">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2"><Activity className="w-5 h-5" style={{ color: accent }} /><span className="font-bold">{t("Letzte Änderungen")}</span></div>
          <button onClick={() => nav(base)} className="text-xs font-semibold flex items-center gap-1" style={{ color: accent }} data-testid="cw-activity-all">{t("Alle Änderungen anzeigen")} <ChevronRight className="w-3.5 h-3.5" /></button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5 gap-3">
          {(overview.recent_changes || []).slice(0, 10).map((ch, i) => {
            const done = ch.state === "done";
            return (
              <div key={i} className="rounded-xl border border-white/5 bg-white/[0.02] p-3.5 flex items-start gap-2.5" data-testid={`cw-change-${i}`}>
                <span className="mt-0.5 shrink-0">{done ? <CheckCircle2 className="w-4 h-4" style={{ color: accent }} /> : <Clock className="w-4 h-4 text-amber-400" />}</span>
                <div className="min-w-0">
                  <div className={`text-sm leading-snug ${done ? "text-gray-200" : "text-amber-200/90"}`}>{ch.text}</div>
                  <div className="text-[10px] uppercase tracking-wider mt-1.5 font-semibold" style={{ color: done ? accent : "#fbbf24" }}>{done ? t("Erledigt") : t("In Arbeit")}</div>
                </div>
              </div>
            );
          })}
          {(!overview.recent_changes || overview.recent_changes.length === 0) && <p className="text-sm text-gray-500 py-2">{t("Noch keine Aktivität.")}</p>}
        </div>
      </Card>
    </div>
  );
}

const SupportCallout = ({ accent, t, onMessage }) => (
  <Card className="p-5 relative overflow-hidden" data-testid="cw-support-callout">
    <div className="absolute -right-10 -top-10 w-32 h-32 rounded-full blur-3xl opacity-10 pointer-events-none" style={{ background: accent }} />
    <div className="relative flex items-center gap-2 mb-1.5">
      <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${accent}1A`, color: accent }}><LifeBuoy className="w-5 h-5" /></span>
      <span className="font-bold text-[15px]">{t("Brauchen Sie Unterstützung?")}</span>
    </div>
    <p className="relative text-sm text-gray-400 mb-4">{t("Unser KEWAG-Team hilft Ihnen schnell und persönlich weiter.")}</p>
    <BtnPrimary accent={accent} className="relative w-full" data-testid="cw-support-message" onClick={onMessage}><MessageCircle className="w-4 h-4" /> {t("Nachricht senden")}</BtnPrimary>
    <div className="relative mt-3 space-y-1.5">
      <a href={`tel:${SUPPORT_PHONE.replace(/\s/g, "")}`} data-testid="cw-support-phone" className="flex items-center gap-2.5 text-sm text-gray-300 hover:text-white transition-colors"><Phone className="w-4 h-4" style={{ color: accent }} /> {SUPPORT_PHONE}</a>
      <a href={`mailto:${SUPPORT_EMAIL}`} data-testid="cw-support-email" className="flex items-center gap-2.5 text-sm text-gray-300 hover:text-white transition-colors"><Mail className="w-4 h-4" style={{ color: accent }} /> {SUPPORT_EMAIL}</a>
    </div>
  </Card>
);

const HealthInfo = ({ accent, t }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button type="button" data-testid="cw-health-info" aria-label={t("Wie wird die Website-Gesundheit berechnet?")} className="text-gray-500 hover:text-white transition-colors inline-flex"><Info className="w-3.5 h-3.5" /></button>
    </PopoverTrigger>
    <PopoverContent align="center" sideOffset={8} className="w-72 bg-[#16161c] border-white/10 text-gray-300 shadow-2xl" data-testid="cw-health-info-pop">
      <div className="text-sm font-bold text-white mb-1">{t("Wie wird die Website-Gesundheit berechnet?")}</div>
      <p className="text-[11px] text-gray-400 mb-2.5 normal-case tracking-normal">{t("Gewichteter Durchschnitt der echten Live-Checks:")}</p>
      <div className="space-y-1 text-[11px]">
        {[["Erreichbarkeit", "45"], ["Sicherheit (SSL)", "30"], ["WordPress", "15"], ["Backup", "5"], ["Formulare", "3"], ["Besuchertracking", "2"]].map(([l, wt]) => (
          <div key={l} className="flex items-center justify-between"><span className="text-gray-300 normal-case tracking-normal">{t(l)}</span><span className="font-bold" style={{ color: accent }}>{wt}</span></div>
        ))}
      </div>
      <div className="mt-3 pt-2.5 border-t border-white/10 space-y-1.5 text-[11px] text-gray-400 normal-case tracking-normal">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: accent }} /> {t("Grün = 100 %")}</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-400" /> {t("Gelb = 60 %")}</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-red-400" /> {t("Rot = 0 %")}</span>
        </div>
        <div className="text-gray-500">{t("Grau (nicht verbunden) zählt nicht mit.")}</div>
        <div className="text-gray-400 pt-0.5">{t("Bewertung:")} ≥ 90 {t("Sehr gut")} · ≥ 75 {t("Gut")} · ≥ 50 {t("Mittel")}</div>
      </div>
    </PopoverContent>
  </Popover>
);

const LegendItem = ({ color, label }) => (
  <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full shrink-0" style={{ background: color }} /> {label}</span>
);

const Fact = ({ label, value, icon: Icon, accent }) => (
  <div className="flex items-center gap-3 rounded-xl bg-white/[0.02] border border-white/5 px-3 py-2.5">
    <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${accent}14`, color: accent }}><Icon className="w-4 h-4" /></span>
    <div className="min-w-0">
      <div className="text-[11px] text-gray-500">{label}</div>
      <div className="text-sm font-semibold text-gray-100 truncate">{value}</div>
    </div>
  </div>
);
